import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  alpha,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { Check, Minus, Plus } from 'lucide-react'
import CustomButton from '../../components/CustomButton'
import { backendClient } from '../../api/backend'
import { fetchPricing } from '../../redux/actions/pricingActions'
import { tracker } from '../../analytics'
import { formatCurrency } from '../../utils/currency'

const GOLD = '#c9a96e'

const SubscribeScreen = () => {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const isDark = theme.palette.mode === 'dark'
  const uiLang = i18n?.language || 'en'

  const { data: subscription } = useSelector(state => state.subscription)
  const { data: pricing, loading: pricingLoading } = useSelector(state => state.pricing)

  const selectedPlanRaw = localStorage.getItem('selectedPlan')
  const selectedPlan = selectedPlanRaw ? JSON.parse(selectedPlanRaw) : null

  const [billing, setBilling] = useState(selectedPlan?.billing || 'annual')
  const [quantity, setQuantity] = useState(selectedPlan?.quantity || 1)
  const [loadingPlan, setLoadingPlan] = useState(null)
  const [error, setError] = useState(null)
  const [displayCurrency, setDisplayCurrency] = useState(
    selectedPlan?.currency === 'USD' ? 'USD' : 'AED'
  )

  const region = subscription?.region || selectedPlan?.region || 'default'

  const isTrialExpired = subscription?.status === 'inactive' ||
    (subscription?.status === 'trial' && subscription?.trial_end && new Date(subscription.trial_end) < new Date())

  // Fetch pricing — re-fetch when region or locale changes
  useEffect(() => {
    dispatch(fetchPricing(region, uiLang))
  }, [dispatch, region, uiLang])

  // Only use pricing if it matches the current region + locale — global fetchPricing calls
  // from authAction/AppRouter can overwrite our region-specific fetch.
  const pricingReady = pricing && pricing.region === region && (pricing.lang === undefined || pricing.lang === uiLang)
  const plans = pricingReady ? (pricing.plans || []) : []
  const baseCurrency = pricingReady ? (pricing.currency || 'AED') : 'AED'
  const currencySymbol = pricingReady ? (pricing.currency_symbol || 'AED') : ''
  const symbolPosition = pricingReady ? (pricing.symbol_position || 'suffix') : 'suffix'
  const canToggleCurrency = baseCurrency === 'AED'

  // Re-fetch if global fetch overwrote our region or locale-specific pricing
  useEffect(() => {
    if (pricing && (pricing.region !== region || (pricing.lang && pricing.lang !== uiLang))) {
      dispatch(fetchPricing(region, uiLang))
    }
  }, [pricing, region, uiLang, dispatch])

  const formatPrice = (price) => {
    if (price == null) return null
    return formatCurrency(
      Number(price),
      baseCurrency,
      currencySymbol,
      symbolPosition,
      canToggleCurrency ? displayCurrency : undefined
    )
  }

  // Find the active volume tier for Pro seat count
  const getActiveTier = (p) => {
    if (!p.volume_tiers || !p.volume_tiers.length) return null
    return p.volume_tiers.find(tier => quantity >= tier.min && quantity <= tier.max) || p.volume_tiers[0]
  }

  const getPerSeatPrice = (p) => {
    const tier = getActiveTier(p)
    if (!tier) {
      // Flat per-seat (no volume tiers): monthly_price is the display price per seat.
      // For annual-only plans, annual_price is null — always fall back to monthly_price.
      return p.monthly_price
    }
    return tier.monthly_per_seat
  }

  const getTotalAnnual = (p) => {
    const tier = getActiveTier(p)
    if (!tier) {
      // Flat per-seat: annual_total is per-seat/yr, multiply by quantity.
      return p.annual_total != null ? p.annual_total * quantity : null
    }
    return tier.annual_per_seat * quantity
  }

  const handleSubscribe = async (planSlug) => {
    setLoadingPlan(planSlug)
    setError(null)
    try {
      const lang = selectedPlan?.lang || 'auto'
      const billingCycle = planSlug === 'pro' ? 'annual' : billing
      const qty = planSlug === 'pro' ? quantity : 1
      const checkoutCurrency = canToggleCurrency ? displayCurrency : baseCurrency
      const sessionData = await backendClient.createStripeSession(
        planSlug, billingCycle, null, region, lang, qty, checkoutCurrency
      )
      tracker.conversion({
        category: 'subscribe_checkout',
        action: 'redirect',
        properties: { plan: planSlug, billing: billingCycle, region },
      })
      tracker.flush()
      window.location.href = sessionData.session_url
    } catch (err) {
      setError(err?.data?.detail || err?.message || t('payment_retry_error'))
      setLoadingPlan(null)
    }
  }

  const ctaLabel = isTrialExpired
    ? t('subscribe_cta', 'Subscribe')
    : t('subscribe_trial_cta', 'Try 7 days free')

  const c = {
    bg: isDark ? '#000' : '#fafafa',
    text: isDark ? '#fff' : '#0a0a0a',
    textMuted: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.55)',
    textSubtle: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.5)',
    textFaint: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
    textBody: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.65)',
    textDim: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
    textHalf: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
    borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.14)',
    borderSoft: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
    borderFaint: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)',
    borderHover: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.3)',
    cardBg: isDark ? 'rgba(255,255,255,0.02)' : '#fff',
    toggleActiveBg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    hoverSurface: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
    primaryBg: isDark ? '#fff' : '#0a0a0a',
    primaryFg: isDark ? '#000' : '#fff',
    primaryHover: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)',
    secondaryBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
    secondaryHover: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)',
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: c.bg,
      py: { xs: 4, md: 6 },
      px: { xs: 2, md: 3 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4, maxWidth: 640 }}>
        <Typography sx={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontWeight: 300,
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          color: c.text,
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          mb: 1,
        }}>
          {isTrialExpired
            ? t('subscribe_trial_expired_title', 'Your free trial has ended')
            : t('subscribe_title', 'Choose your plan')}
        </Typography>
        <Typography sx={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
          color: c.textMuted,
          mb: 3,
        }}>
          {isTrialExpired
            ? t('subscribe_trial_expired_subtitle', 'Choose a plan to continue using Lawis')
            : t('subscribe_subtitle', 'Select a plan to get started')}
        </Typography>

        <Box sx={{ display: 'inline-flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          {/* Billing toggle */}
          <Box sx={{
            display: 'inline-flex',
            borderRadius: '12px',
            border: `1px solid ${c.border}`,
            overflow: 'hidden',
          }}>
            {['monthly', 'annual'].map(b => (
              <Box
                key={b}
                onClick={() => setBilling(b)}
                sx={{
                  px: 3, py: 1.2,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 1,
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: billing === b ? c.text : c.textDim,
                  backgroundColor: billing === b ? c.toggleActiveBg : 'transparent',
                  transition: 'all 0.2s',
                }}
              >
                {b === 'monthly' ? t('billing_monthly', 'Monthly') : t('billing_annual', 'Annual')}
                {b === 'annual' && (
                  <Chip label="-20%" size="small" sx={{
                    height: 20, fontSize: 11, fontWeight: 700,
                    backgroundColor: '#10b981', color: '#fff',
                  }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Currency toggle (AED base only) */}
          {canToggleCurrency && (
            <Box sx={{
              display: 'inline-flex',
              borderRadius: '12px',
              border: `1px solid ${c.border}`,
              overflow: 'hidden',
            }}>
              {['AED', 'USD'].map(cur => (
                <Box
                  key={cur}
                  onClick={() => setDisplayCurrency(cur)}
                  sx={{
                    px: 3, py: 1.2,
                    cursor: 'pointer',
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: displayCurrency === cur ? c.text : c.textDim,
                    backgroundColor: displayCurrency === cur ? c.toggleActiveBg : 'transparent',
                    transition: 'all 0.2s',
                  }}
                >
                  {cur}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Plan cards */}
      {(pricingLoading || !pricingReady) ? (
        <CircularProgress sx={{ my: 8, color: GOLD }} />
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 2.5,
          maxWidth: 1280,
          width: '100%',
          alignItems: 'start',
        }}>
          {plans.map((p) => {
            const isPro = p.slug === 'pro'
            const isEnterprise = p.is_custom_pricing
            const isHighlighted = isPro
            const perSeatPrice = isPro ? getPerSeatPrice(p) : null
            const totalAnnual = isPro ? getTotalAnnual(p) : null
            const price = isPro ? perSeatPrice : (billing === 'annual' ? p.annual_price : p.monthly_price)
            const annualSavings = p.monthly_price && p.annual_price
              ? (p.monthly_price * 12) - (p.annual_total || p.annual_price * 12)
              : null

            return (
              <Box key={p.slug} sx={{
                borderRadius: '16px',
                border: isHighlighted
                  ? `1.5px solid ${GOLD}`
                  : `1px solid ${c.borderSoft}`,
                backgroundColor: c.cardBg,
                boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.03)',
                p: { xs: 3, md: 3.5 },
                display: 'flex',
                flexDirection: 'column',
              }}>
                {/* Badges + Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontWeight: 700,
                    fontSize: '20px',
                    color: c.text,
                  }}>
                    {p.display_name}
                  </Typography>
                  {isPro && p.annual_only && (
                    <Chip label={t('annual_only', 'ANNUAL ONLY')} size="small" sx={{
                      height: 22, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                      backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa',
                      border: '1px solid rgba(59,130,246,0.25)',
                    }} />
                  )}
                  {isHighlighted && (
                    <Chip label={t('best_roi', 'BEST ROI')} size="small" sx={{
                      height: 22, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                      backgroundColor: `${alpha(GOLD, 0.15)}`, color: GOLD,
                      border: `1px solid ${alpha(GOLD, 0.3)}`,
                    }} />
                  )}
                </Box>

                {/* Description */}
                <Typography sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '13px',
                  color: c.textSubtle,
                  mb: 2.5,
                  minHeight: 36,
                }}>
                  {p.tagline || ''}
                </Typography>

                {/* Price */}
                {isEnterprise ? (
                  <Typography sx={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 'clamp(2rem, 3vw, 2.5rem)',
                    color: c.text,
                    mb: 2,
                  }}>
                    {t('custom_pricing', 'Custom')}
                  </Typography>
                ) : (
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 800,
                        fontSize: 'clamp(2.2rem, 4vw, 3rem)',
                        color: c.text,
                        lineHeight: 1,
                      }}>
                        {formatPrice(price)}
                      </Typography>
                      <Typography sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: '14px',
                        color: c.textSubtle,
                      }}>
                        {isPro
                          ? `/${t('user', 'user')}/${t('month_short', 'mo')}`
                          : `/${t('month_short', 'mo')}`}
                      </Typography>
                    </Box>
                    {/* Annual savings or total */}
                    {isPro && totalAnnual ? (
                      <Typography sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: '12px',
                        color: c.textFaint,
                        mt: 0.5,
                      }}>
                        {t('total', 'Total')}: {formatPrice(totalAnnual)}/{t('year_short', 'yr')} {t('for', 'for')} {quantity} {quantity > 1 ? t('users', 'users') : t('user', 'user')}
                      </Typography>
                    ) : billing === 'annual' && annualSavings > 0 ? (
                      <Typography sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: '12px',
                        color: c.textFaint,
                        mt: 0.5,
                      }}>
                        {t('billed_annually', 'Billed annually')} · {t('save', 'Save')} {formatPrice(annualSavings)}/{t('year_short', 'yr')}
                      </Typography>
                    ) : null}
                  </Box>
                )}

                {/* Volume tiers for Pro */}
                {isPro && p.volume_tiers && p.volume_tiers.length > 0 && (
                  <Box sx={{
                    border: `1px solid ${c.borderSoft}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    mb: 2,
                  }}>
                    {p.volume_tiers.map((tier, i) => {
                      const isActive = quantity >= tier.min && quantity <= tier.max
                      return (
                        <Box key={i} sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          px: 2, py: 1,
                          backgroundColor: isActive ? alpha(GOLD, 0.08) : 'transparent',
                          borderBottom: i < p.volume_tiers.length - 1 ? `1px solid ${c.borderFaint}` : 'none',
                          borderLeft: isActive ? `2px solid ${GOLD}` : '2px solid transparent',
                        }}>
                          <Typography sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: '13px',
                            color: isActive ? c.text : c.textDim,
                            fontWeight: isActive ? 600 : 400,
                          }}>
                            {tier.min}-{tier.max} {t('users', 'users')}
                          </Typography>
                          <Typography sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: '13px',
                            color: isActive ? c.text : c.textDim,
                            fontWeight: isActive ? 700 : 400,
                          }}>
                            {formatPrice(tier.monthly_per_seat)}/{t('user_abbr', 'user')}/{t('month_short', 'mo')}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Box>
                )}

                {/* Seat selector for Pro */}
                {isPro && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: `1px solid ${c.border}`,
                    borderRadius: '10px',
                    px: 2, py: 1.2,
                    mb: 2.5,
                  }}>
                    <Typography sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '14px',
                      color: c.textHalf,
                    }}>
                      {t('users_label', 'Users')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        disabled={quantity <= 1}
                        sx={{
                          border: `1px solid ${c.border}`,
                          color: c.textHalf,
                          width: 32, height: 32,
                          '&:hover': { borderColor: c.borderHover },
                        }}
                      >
                        <Minus size={14} />
                      </IconButton>
                      <Typography sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 700,
                        fontSize: '16px',
                        color: c.text,
                        minWidth: 24,
                        textAlign: 'center',
                      }}>
                        {quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setQuantity(q => Math.min(p.max_seats || 10, q + 1))}
                        disabled={quantity >= (p.max_seats || 10)}
                        sx={{
                          border: `1px solid ${c.border}`,
                          color: c.textHalf,
                          width: 32, height: 32,
                          '&:hover': { borderColor: c.borderHover },
                        }}
                      >
                        <Plus size={14} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* CTA Button */}
                {isEnterprise ? (
                  <Box
                    onClick={() => window.open('https://lawis.ai/contact', '_blank')}
                    sx={{
                      py: 1.5,
                      borderRadius: '10px',
                      border: `1px solid ${c.borderStrong}`,
                      textAlign: 'center',
                      cursor: 'pointer',
                      mb: 3,
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: c.borderHover, backgroundColor: c.hoverSurface },
                    }}
                  >
                    <Typography sx={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontWeight: 600,
                      fontSize: '14px',
                      color: c.text,
                    }}>
                      {t('contact_sales', 'Contact sales')}
                    </Typography>
                  </Box>
                ) : (
                  <CustomButton
                    text={loadingPlan === p.slug
                      ? <CircularProgress size={18} color="inherit" />
                      : ctaLabel}
                    outlined={false}
                    onClick={() => handleSubscribe(p.slug)}
                    disabled={!!loadingPlan}
                    sx={{
                      width: '100%',
                      py: 1.5,
                      borderRadius: '10px',
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      mb: 3,
                      backgroundColor: isHighlighted ? c.primaryBg : c.secondaryBg,
                      color: isHighlighted ? c.primaryFg : c.text,
                      border: isHighlighted ? 'none' : `1px solid ${c.border}`,
                      '&:hover': {
                        backgroundColor: isHighlighted ? c.primaryHover : c.secondaryHover,
                      },
                    }}
                  />
                )}

                {/* Feature list */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                  {(p.feature_descriptions || []).map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                      <Check size={15} color="#10b981" style={{ marginTop: 2, flexShrink: 0 }} />
                      <Typography sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: '13px',
                        color: c.textBody,
                        lineHeight: 1.5,
                      }}>
                        {f}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )
          })}
        </Box>
      )}

      {/* Error */}
      {error && (
        <Typography sx={{
          mt: 3,
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '14px',
          color: '#f87171',
          textAlign: 'center',
        }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}

export default SubscribeScreen
